import React, { useState } from 'react'
import { Flex, Box, Text, Icon, ColorType } from '@clear/design-system'

import USFlagIcon from '@assets/svgs/us-flag-icon'
import GreenLockIcon from '@assets/svgs/green-lock-icon'
import BlueGovBldgIcon from '@assets/svgs/blue-gov-bldg-icon'
import { colors } from '../../../styles/theme'
import { useFlag } from '@hooks/useFlag'
import {
  YOUNG_ADULT_PROMOTION_NAME,
  YOUNG_ADULT_PROMOTION_PATH,
} from '@api/constants'
import { useRouter } from 'next/router'

export const GovernmentSecurityBanner = () => {
  const [shouldShowDrawer, setShouldShowDrawer] = useState(false)

  const handleOnClick = () => {
    setShouldShowDrawer((prevState) => !prevState)
  }
  return (
    <Box>
      <TopBanner isExpanded={shouldShowDrawer} onClick={handleOnClick} />
      <BottomBanner />
    </Box>
  )
}

const TopBanner = ({
  isExpanded,
  onClick,
}: {
  isExpanded: boolean
  onClick: () => void
}) => {
  return (
    <>
      <Flex
        py={4}
        px={[4, 9]}
        onClick={onClick}
        sx={{ cursor: 'pointer' }}
        backgroundColor="neutralsBackgroundGray"
      >
        <Box flex="0 0 1.5rem">
          <USFlagIcon />
        </Box>
        <Text as="div" fontSize={0} fontWeight="300">
          An official website of the United States government&nbsp;
          <Text display="inline-block" color="brandDarkBlue">
            Here's how you know
          </Text>
        </Text>
        <Box position="relative" ml="0.5rem" mb="0" flex="0">
          <Box position="absolute" top="-0.25rem" left="-0.5rem">
            <Icon
              name={isExpanded ? 'drop-up' : 'drop-down'}
              color={colors.brandMidnightBlue as ColorType}
            />
          </Box>
        </Box>
      </Flex>
      {isExpanded && (
        <Box
          display={['block', 'flex']}
          justifyContent="center"
          pt={2}
          px={4}
          pb="1.5rem"
          mx={1}
          fontSize={1}
        >
          <Flex width="100%" maxWidth="37rem" mr={[0, 5]}>
            <Box flex="0 2.5rem" mr={5}>
              <BlueGovBldgIcon />
            </Box>
            <Box flex="1">
              <Text
                as="h3"
                mb={2}
                fontFamily="medium"
                fontSize={1}
                fontWeight="700"
              >
                The .gov means it’s official.
              </Text>
              <Text as="p" fontWeight="300">
                Federal government websites often end in .gov or .mil. Before
                sharing sensitive information, make sure you’re on a federal
                government site.
              </Text>
            </Box>
          </Flex>
          <Flex width="100%" maxWidth="37rem" mt={[5, 0]}>
            <Box flex="0 2.5rem" mr={5}>
              <GreenLockIcon />
            </Box>
            <Box flex="1">
              <Text
                as="h3"
                mb={2}
                fontFamily="medium"
                fontSize={1}
                fontWeight="700"
              >
                The site is secure.
              </Text>
              <Text as="p" fontWeight="300">
                The https:// ensures that you are connecting to the official
                website and that any information you provide is encrypted and
                transmitted securely.
              </Text>
            </Box>
          </Flex>
        </Box>
      )}
    </>
  )
}

const BottomBanner = () => {
  const router = useRouter()
  const youngAdultFlag = useFlag('youngAdultDiscount')?.showPromoItems
  const showMaintenanceBanner = useFlag('maintenance')?.banner

  // Show young adult banner only if:
  // 1. "Show promo items" flag is on
  // 2. We're not in the renewal flow at all. YA discount is for new enrollments only.
  // 3. We're not on the confirmation page, which has its own discrete banner
  if (
    youngAdultFlag &&
    !router.asPath.startsWith('/renewal') &&
    !router.asPath.includes('confirmation')
  ) {
    return <YoungAdultBanner />
  }

  if (!showMaintenanceBanner) {
    return null
  }

  return <MaintenanceBanner />
}

const MaintenanceBanner = () => {
  const bannerCopy =
    "We're currently experiencing high call and email volume. Wait times may be longer than usual. To check your application status, "
  return (
    <Flex
      py={4}
      className="justify-center text-lg gap-x-2 bg-clear-green-banner"
    >
      <Icon mr={[2, 0]} name="info" />
      <Text
        as="div"
        fontFamily="Inter-SemiBold"
        fontWeight="600"
        lineHeight="22px"
      >
        {bannerCopy}
        <Text as="a" target="_blank" href="/status" className="underline">
          click here.
        </Text>
      </Text>
    </Flex>
  )
}

const YoungAdultBanner = () => (
  <Flex
    py={4}
    className="justify-center text-lg gap-x-2"
    backgroundColor="brandDarkBlue"
    data-cy="young-adult-header-banner"
  >
    <Text
      as="div"
      fontFamily="Inter-Regular"
      fontWeight="300"
      lineHeight="22px"
      color="neutralsWhite"
    >
      {YOUNG_ADULT_PROMOTION_NAME} — 30 years old or under? Starting May 1, get
      $20 off your TSA&nbsp;PreCheck® application, valid until May 31, 2026.{' '}
      <Text
        as="a"
        target="_blank"
        href={YOUNG_ADULT_PROMOTION_PATH}
        className="underline"
      >
        Learn More
      </Text>
    </Text>
  </Flex>
)
